import React from "react";
import { Link } from "react-router-dom";
import "../css/privacy.scss";
import "../css/policies.scss";

const CONTACT_EMAIL = "globe@ioomparade.online";

export const policyDocuments = [
  {
    slug: "orders",
    title: "Order Policy",
    eyebrow: "From cart to confirmation",
    lead: "How ioomparade reviews, accepts, changes, and fulfils orders placed through this store.",
    sections: [
      {
        title: "Placing an order",
        body: [
          "An item in your cart is not reserved. Your order is submitted when you complete the hosted payment process. Please review the products, quantities, delivery details, currency, shipping charge, and total before paying.",
          "You must provide accurate contact and delivery information and be authorized to use the selected payment method.",
        ],
      },
      {
        title: "Order acknowledgement and acceptance",
        body: [
          "A payment screen, payment authorization, or automated message acknowledges that your order was received; it does not by itself require ioomparade to accept the order. Acceptance occurs when we confirm fulfilment or dispatch, subject to any mandatory consumer rights.",
        ],
      },
      {
        title: "Availability",
        body: [
          "Products remain subject to availability. If an item cannot be supplied, we may contact you to offer a reasonable alternative or cancel the affected item and arrange a refund to the original payment method.",
        ],
      },
      {
        title: "Order review and refusal",
        body: [
          "We may hold, limit, or refuse an order for legitimate reasons such as an apparent pricing error, unavailable stock, an incomplete address, failed payment, suspected fraud, unusually large quantities, sanctions or trade restrictions, or suspected resale activity. We will not refuse service for an unlawful discriminatory reason.",
        ],
      },
      {
        title: "Order records",
        body: [
          "Keep your order reference and payment confirmation. Contact us promptly if the confirmation does not match what you intended to buy.",
        ],
      },
      {
        title: "Related policies",
        body: [
          "Shipping, cancellation, payment, returns, and international-order terms form part of this Order Policy. Mandatory rights under applicable law always continue to apply.",
        ],
      },
    ],
    related: [
      { label: "Payment & Billing", to: "/policies/payments" },
      { label: "Cancellation Policy", to: "/policies/cancellations" },
      { label: "Delivery & Shipping", to: "/shipping" },
    ],
  },
  {
    slug: "payments",
    title: "Payment & Billing Policy",
    eyebrow: "Secure hosted payment",
    lead: "Payment methods, authorization, currency, billing corrections, and chargeback information for ioomparade orders.",
    sections: [
      {
        title: "Payment provider",
        body: [
          "Payment is completed on a secure page operated by our payment service provider. ioomparade's checkout form does not ask for or store full card numbers, card security codes, or card authentication credentials.",
          "The provider applies its own privacy, security, verification, and availability rules. A payment option shown by the provider may depend on your location, device, currency, or risk review.",
        ],
      },
      {
        title: "Currency and charges",
        body: [
          "Store prices and checkout totals are displayed in United States dollars unless clearly stated otherwise. Your bank or card issuer may apply currency-conversion, international-transaction, or other fees that are not charged or controlled by ioomparade.",
        ],
      },
      {
        title: "Authorization",
        body: [
          "By submitting payment, you confirm that you are authorized to use the payment method and authorize the applicable order total. An authorization may appear as pending before it settles or is released by your provider.",
        ],
      },
      {
        title: "Failed or duplicate payments",
        body: [
          "A declined payment may be retried with corrected details or another method offered on the payment page. If you believe you were charged twice, contact us with the order reference and non-sensitive evidence of the transactions. Never email a full card number or security code.",
        ],
      },
      {
        title: "Taxes and duties",
        body: [
          "Taxes collected by the store, if any, are shown before payment. Import duties, customs charges, local taxes, and carrier handling fees assessed after dispatch may be payable by the recipient unless applicable law requires another arrangement.",
        ],
      },
      {
        title: "Refunds and disputes",
        body: [
          "Approved refunds are sent to the original payment method where practicable. Processing time after we issue a refund is controlled by the payment provider and your financial institution. Please contact us first about a billing concern so we have an opportunity to investigate; this does not remove any lawful right to contact your payment provider.",
        ],
      },
    ],
    related: [
      { label: "Returns & Refunds", to: "/returns" },
      { label: "Pricing & Promotions", to: "/policies/pricing-promotions" },
      { label: "Privacy Policy", to: "/privacy" },
    ],
  },
  {
    slug: "cancellations",
    title: "Cancellation & Order Changes Policy",
    eyebrow: "Tell us as soon as possible",
    lead: "When an order may be changed or cancelled and what happens after fulfilment begins.",
    sections: [
      {
        title: "How to request a change",
        body: [
          "Email us as soon as possible with your order reference and the exact change requested. Do not send payment credentials. A request is not confirmed until ioomparade replies that the change or cancellation was completed.",
        ],
      },
      {
        title: "Before packing or dispatch",
        body: [
          "We will make a reasonable effort to update an address, remove an item, or cancel an order before packing begins. Fast fulfilment means we cannot guarantee that a message will be seen in time.",
        ],
      },
      {
        title: "After dispatch",
        body: [
          "Orders that have entered the carrier network cannot normally be cancelled or rerouted by us. You may request an eligible return after delivery under the Returns & Refunds Policy.",
        ],
      },
      {
        title: "Address changes",
        body: [
          "For fraud prevention, a material address change may require cancellation and a new order. We cannot guarantee recovery or replacement when an incorrect address supplied by the customer causes delivery to fail.",
        ],
      },
      {
        title: "Cancellation by ioomparade",
        body: [
          "We may cancel all or part of an order for stock, payment, pricing, safety, legal, delivery, or fraud-prevention reasons. If payment was captured for a cancelled item, we will arrange a refund to the original payment method.",
        ],
      },
      {
        title: "Mandatory cancellation rights",
        body: [
          "Nothing in this policy limits a cooling-off, withdrawal, or cancellation right that cannot lawfully be excluded in your location. Contact us if you believe such a right applies.",
        ],
      },
    ],
    related: [
      { label: "Order Policy", to: "/policies/orders" },
      { label: "Returns & Refunds", to: "/returns" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    slug: "pricing-promotions",
    title: "Pricing & Promotions Policy",
    eyebrow: "Clear prices at checkout",
    lead: "How product prices, promotions, taxes, shipping charges, and corrections are handled.",
    sections: [
      {
        title: "Displayed prices",
        body: [
          "Prices are shown in United States dollars unless the page expressly states another currency. The final checkout screen displays the product subtotal, the applicable shipping charge, and the total presented for payment.",
        ],
      },
      {
        title: "Price changes",
        body: [
          "We may change a price prospectively. A later price change does not normally change an accepted order, except where the displayed price was an obvious error or correction is required by law.",
        ],
      },
      {
        title: "Promotions",
        body: [
          "A promotion may have a stated period, product scope, minimum spend, redemption limit, territory, or other condition. Unless the offer says otherwise, promotions cannot be combined, exchanged for cash, applied retrospectively, or used to buy unavailable items.",
        ],
      },
      {
        title: "Errors",
        body: [
          "If a material pricing or product error affects your order, we may contact you for instructions or cancel the affected item. If payment was already captured, the cancelled amount will be refunded to the original payment method.",
        ],
      },
      {
        title: "Taxes, duties, and third-party fees",
        body: [
          "Checkout shows taxes collected by us, if any. Your bank, payment provider, customs authority, or carrier may impose separate charges outside our control.",
        ],
      },
      {
        title: "Fair use",
        body: [
          "We may reject manipulated, duplicated, automated, or otherwise abusive promotion use. This does not affect legitimate consumer rights.",
        ],
      },
    ],
    related: [
      { label: "Payment & Billing", to: "/policies/payments" },
      { label: "Order Policy", to: "/policies/orders" },
      { label: "Terms of Service", to: "/terms" },
    ],
  },
  {
    slug: "international-orders",
    title: "International Orders & Customs Policy",
    eyebrow: "Cross-border delivery",
    lead: "Important information for orders shipped across national or customs borders.",
    sections: [
      {
        title: "Available destinations",
        body: [
          "We accept delivery only to countries and regions shown in the checkout selector. Availability may change because of carrier coverage, operational limits, trade controls, or other legal requirements.",
        ],
      },
      {
        title: "Customs and import formalities",
        body: [
          "International parcels may be inspected and may require customs information. The recipient is responsible for providing lawful and accurate information needed for clearance.",
        ],
      },
      {
        title: "Duties and local charges",
        body: [
          "Unless checkout expressly says they are included, import duties, local taxes, brokerage, storage, and carrier handling fees are the recipient's responsibility. These amounts are set by third parties and cannot be predicted or controlled by ioomparade.",
        ],
      },
      {
        title: "Delivery estimates",
        body: [
          "Customs review, weather, public holidays, local service interruptions, or incomplete clearance information can extend transit time. A delivery estimate is not a guaranteed arrival date unless the checkout specifically states that it is guaranteed.",
        ],
      },
      {
        title: "Restricted items and destinations",
        body: [
          "We do not knowingly ship where the transaction or product is prohibited. An order may be refused or cancelled when required by export, import, sanctions, carrier, or product-safety rules.",
        ],
      },
      {
        title: "Refused or unclaimed parcels",
        body: [
          "If a parcel is refused, not collected, or returned because duties were not paid or required information was not supplied, any refund will be assessed after the item returns to us. Where permitted by law, unrecoverable shipping, return, duty, or carrier costs may be deducted.",
        ],
      },
    ],
    related: [
      { label: "Delivery & Shipping", to: "/shipping" },
      { label: "Returns & Refunds", to: "/returns" },
      { label: "Consumer Rights", to: "/policies/consumer-rights" },
    ],
  },
  {
    slug: "warranty",
    title: "Warranty & Defective Items Policy",
    eyebrow: "Products should arrive as described",
    lead: "How to report a fault and how express warranties interact with your statutory rights.",
    sections: [
      {
        title: "Legal guarantees",
        body: [
          "Products remain covered by any non-excludable guarantee, conformity right, or implied warranty that applies where you live. This policy does not replace or reduce those rights.",
        ],
      },
      {
        title: "Voluntary warranties",
        body: [
          "Unless a product page or written document expressly provides a separate warranty, ioomparade does not add a fixed-duration manufacturer's warranty beyond applicable legal rights.",
        ],
      },
      {
        title: "Reporting a defect",
        body: [
          "Contact us promptly with the order reference, a clear description of the issue, and photographs when useful. Keep the item and packaging until we confirm the next step. Do not send an item without authorization.",
        ],
      },
      {
        title: "Assessment",
        body: [
          "We may request reasonable evidence or return of the product to determine whether the issue is a manufacturing defect, transit damage, normal variation, normal wear, misuse, accidental damage, unauthorized alteration, or failure to follow care instructions.",
        ],
      },
      {
        title: "Available remedies",
        body: [
          "Depending on the facts, stock, location, and applicable law, an appropriate remedy may be repair, replacement, refund, price reduction, or another agreed solution. We will cover reasonable return costs when the law or an accepted defect claim requires us to do so.",
        ],
      },
      {
        title: "Consumable and natural variation",
        body: [
          "Expected depletion of consumables and minor differences in color, grain, texture, handmade finish, screen rendering, or dimensions within the stated tolerance are not necessarily defects. Material differences from the description should still be reported.",
        ],
      },
    ],
    related: [
      { label: "Product Information & Safety", to: "/policies/product-safety" },
      { label: "Returns & Refunds", to: "/returns" },
      { label: "Consumer Rights", to: "/policies/consumer-rights" },
    ],
  },
  {
    slug: "product-safety",
    title: "Product Information, Care & Safety Policy",
    eyebrow: "Use products as intended",
    lead: "Guidance on product descriptions, variations, care, safe use, and safety notices.",
    sections: [
      {
        title: "Product information",
        body: [
          "We aim to describe the main features, materials, dimensions, quantity, compatibility, and intended use accurately. Images may include styling props that are not part of the product. Your screen may display color differently.",
        ],
      },
      {
        title: "Before use",
        body: [
          "Read labels, packaging, care instructions, and warnings supplied with the item. Inspect a product before use and stop using it if it is damaged or behaves unexpectedly.",
        ],
      },
      {
        title: "Stationery and tool safety",
        body: [
          "Keep sharp tools, small parts, inks, paints, adhesives, magnets, and other unsuitable items away from children and pets. Use adequate ventilation and protect surfaces when a product or medium calls for it. Do not ingest art or writing materials unless they are expressly labelled for that purpose.",
        ],
      },
      {
        title: "Compatibility and testing",
        body: [
          "Paper, ink, paint, finishes, and tools can react differently. Test unfamiliar combinations on an inconspicuous area and follow the relevant manufacturer's instructions. A descriptive compatibility note is general guidance, not a guarantee for every third-party material.",
        ],
      },
      {
        title: "Care",
        body: [
          "Store products in suitable dry conditions, avoid unnecessary heat or direct sunlight, and clean them only by methods appropriate for the listed material. Damage caused by unsuitable storage, cleaning, alteration, or use may not qualify as a defect.",
        ],
      },
      {
        title: "Safety notices and recalls",
        body: [
          "If we learn of a material safety issue, we may publish a notice and contact affected customers when reliable contact details and legal grounds permit. Follow recall instructions promptly and do not continue using a recalled product.",
        ],
      },
    ],
    related: [
      { label: "Warranty & Defective Items", to: "/policies/warranty" },
      { label: "Order Policy", to: "/policies/orders" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    slug: "consumer-rights",
    title: "Consumer Rights Notice",
    eyebrow: "Your mandatory rights remain",
    lead: "A plain-language statement about the relationship between store policies and applicable consumer law.",
    sections: [
      {
        title: "Mandatory rights",
        body: [
          "Consumer laws may give you rights concerning product quality, accurate descriptions, delivery, cancellation, repair, replacement, refund, or unfair contract terms. Those rights vary by location and may not be waived by a store policy.",
        ],
      },
      {
        title: "Policies do not reduce legal protection",
        body: [
          "If a term on this site conflicts with a right that applicable law says cannot be excluded, the mandatory right prevails and the remaining terms continue to operate as far as permitted.",
        ],
      },
      {
        title: "Cooling-off and withdrawal",
        body: [
          "Some jurisdictions provide a period to withdraw from certain online purchases, with exceptions. If you wish to rely on such a right, contact us before the deadline and include your order reference and an unambiguous statement that you wish to withdraw.",
        ],
      },
      {
        title: "Faulty or misdescribed goods",
        body: [
          "A store return window does not necessarily limit a longer legal remedy for faulty, unsafe, counterfeit, or materially misdescribed goods. Contact us with evidence appropriate to the issue.",
        ],
      },
      {
        title: "Where to get help",
        body: [
          "Contact ioomparade first so we can review the matter. You may also have the right to seek help from a local consumer-protection body, payment provider, regulator, court, or other authorized dispute-resolution service.",
        ],
      },
      {
        title: "No legal advice",
        body: [
          "This notice is general information and is not legal advice. Your rights depend on the facts and the law applicable to you.",
        ],
      },
    ],
    related: [
      { label: "Returns & Refunds", to: "/returns" },
      { label: "Complaints & Resolution", to: "/policies/complaints" },
      { label: "Terms of Service", to: "/terms" },
    ],
  },
  {
    slug: "privacy-choices",
    title: "Privacy Choices & Data Requests",
    eyebrow: "Control your information",
    lead: "How to make a privacy request and what to expect from the verification process.",
    sections: [
      {
        title: "Available requests",
        body: [
          "Depending on the law that applies, you may ask to access, correct, delete, restrict, object to processing, withdraw consent, or receive a portable copy of personal information. You may also ask for information about categories of data, sources, purposes, and recipients.",
        ],
      },
      {
        title: "How to submit a request",
        body: [
          `Email ${CONTACT_EMAIL} with the subject “Privacy Request.” Describe the request and provide enough information to locate the relevant interaction. Do not email payment card details, passwords, or unnecessary identification documents.`,
        ],
      },
      {
        title: "Verification",
        body: [
          "We may ask for proportionate information to confirm identity, authority, and the scope of the request. An authorized representative may be asked for proof of authority. We will use verification information only for security and request handling.",
        ],
      },
      {
        title: "Sale, sharing, and targeted advertising",
        body: [
          "ioomparade does not currently sell personal information or share it for cross-context behavioural advertising, and this site does not currently place advertising cookies. If that changes, we will update the Privacy and Cookie Policies and provide any legally required opt-out method before the new practice begins.",
        ],
      },
      {
        title: "Appeals and complaints",
        body: [
          "If we deny a request, you may reply and ask us to reconsider. Where applicable, you may also complain to the data-protection or privacy authority that serves your location.",
        ],
      },
      {
        title: "Non-discrimination",
        body: [
          "We will not unlawfully discriminate against you for exercising a privacy right. Some information remains necessary to provide a requested transaction or comply with legal duties.",
        ],
      },
    ],
    related: [
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Cookie Policy", to: "/cookies" },
      { label: "Security Policy", to: "/policies/security" },
    ],
  },
  {
    slug: "security",
    title: "Security & Vulnerability Reporting Policy",
    eyebrow: "Help protect the store",
    lead: "Our security approach and a safe channel for reporting suspected vulnerabilities or account concerns.",
    sections: [
      {
        title: "Security approach",
        body: [
          "We use proportionate administrative and technical measures intended to protect store and order information. Payment-card entry is handled on the payment provider's hosted page rather than the ioomparade checkout form. No internet service can guarantee absolute security.",
        ],
      },
      {
        title: "Protect yourself",
        body: [
          "Use an up-to-date browser, verify the site address before paying, and do not send full card numbers or security codes by email. Treat unexpected payment or delivery messages cautiously.",
        ],
      },
      {
        title: "Report a concern",
        body: [
          `Email ${CONTACT_EMAIL} with the subject “Security Report.” Include the affected page, a clear description, steps to reproduce where safe, and your contact details. Remove personal data and do not include live credentials.`,
        ],
      },
      {
        title: "Good-faith testing boundaries",
        body: [
          "Do not access another person's data, use social engineering, disrupt service, run denial-of-service tests, send spam, install malware, alter or delete data, make fraudulent purchases, or test third-party systems without their written permission.",
        ],
      },
      {
        title: "Our response",
        body: [
          "We will review credible reports and may request more detail. We do not promise a response deadline, public credit, payment, or bounty unless agreed in writing before disclosure.",
        ],
      },
      {
        title: "Security incidents",
        body: [
          "If a confirmed incident requires notice under applicable law, we will provide notice to affected people or authorities as required. Please report suspected phishing that uses the ioomparade name.",
        ],
      },
    ],
    related: [
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Privacy Choices", to: "/policies/privacy-choices" },
      { label: "Acceptable Use", to: "/policies/acceptable-use" },
    ],
  },
  {
    slug: "acceptable-use",
    title: "Acceptable Use Policy",
    eyebrow: "Use the site responsibly",
    lead: "Rules that protect customers, the store, third parties, and the integrity of online transactions.",
    sections: [
      {
        title: "Lawful use",
        body: [
          "You may use the site only for lawful personal or authorized business purposes. You must comply with applicable laws and the rights of other people.",
        ],
      },
      {
        title: "Security and interference",
        body: [
          "Do not bypass access controls, probe for vulnerabilities outside the Security Policy, introduce malicious code, overload infrastructure, interfere with checkout, or disrupt another visitor's use.",
        ],
      },
      {
        title: "Fraud and impersonation",
        body: [
          "Do not use stolen payment details, provide deceptive order information, impersonate another person or organization, evade purchase limits, abuse refunds, or manipulate promotions.",
        ],
      },
      {
        title: "Content and communications",
        body: [
          "Do not send unlawful, infringing, threatening, abusive, discriminatory, deceptive, obscene, or privacy-invasive material. Do not submit trade secrets, confidential third-party information, or content you do not have the right to provide.",
        ],
      },
      {
        title: "Automated access",
        body: [
          "Do not scrape, crawl, copy, or extract site content at scale, create excessive automated requests, or use bots to reserve inventory or complete purchases without written permission. Ordinary search-engine indexing and accessibility tools are not prohibited.",
        ],
      },
      {
        title: "Enforcement",
        body: [
          "We may reasonably investigate suspected misuse and limit access, cancel affected orders, preserve evidence, or contact service providers or authorities where lawful and proportionate. Mandatory consumer rights remain unaffected.",
        ],
      },
    ],
    related: [
      { label: "Terms of Service", to: "/terms" },
      { label: "Security Policy", to: "/policies/security" },
      { label: "Intellectual Property", to: "/policies/intellectual-property" },
    ],
  },
  {
    slug: "intellectual-property",
    title: "Intellectual Property & Takedown Policy",
    eyebrow: "Respecting creative rights",
    lead: "Rules for using ioomparade materials and reporting content that may infringe another party's rights.",
    sections: [
      {
        title: "Site materials",
        body: [
          "Unless a different owner or licence is identified, the ioomparade name, site-specific text, original graphics, product photography, page arrangement, and other original materials are owned by or licensed to ioomparade and protected by applicable intellectual-property laws.",
        ],
      },
      {
        title: "Permitted use",
        body: [
          "You may view the site, make purchases, and keep a reasonable personal copy of an order or policy page. No other licence is granted. Do not reproduce, sell, republish, remove rights notices from, or create confusingly similar commercial materials from site content without permission or another lawful basis.",
        ],
      },
      {
        title: "Third-party rights",
        body: [
          "Names, marks, or content belonging to third parties remain the property of their respective owners. A reference used to describe compatibility or a payment method does not imply sponsorship or affiliation unless expressly stated.",
        ],
      },
      {
        title: "Customer submissions",
        body: [
          "If you send feedback, images, or other content, you confirm you have the right to do so. You grant ioomparade a non-exclusive licence to review, reproduce, and use the submission only as reasonably needed to respond, support an order, investigate a claim, or use it in another way you expressly authorize.",
        ],
      },
      {
        title: "Reporting possible infringement",
        body: [
          `Email ${CONTACT_EMAIL} with the subject “Rights Notice.” Identify the protected work or right, the exact site location, your contact details, the basis for your claim, and a good-faith statement that the disputed use is not authorized. If you act for an owner, include evidence of authority.`,
        ],
      },
      {
        title: "Review and response",
        body: [
          "We may request clarification, preserve relevant records, restrict disputed content during review, notify the affected contributor where appropriate, and restore or remove material based on the available evidence and applicable law. Knowingly false notices may create liability.",
        ],
      },
    ],
    related: [
      { label: "Acceptable Use", to: "/policies/acceptable-use" },
      { label: "Product Information & Safety", to: "/policies/product-safety" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    slug: "accessibility",
    title: "Accessibility Statement",
    eyebrow: "A store for more people",
    lead: "Our continuing approach to accessible browsing, shopping, and customer support.",
    sections: [
      {
        title: "Our commitment",
        body: [
          "ioomparade aims to make this store usable for people with a wide range of abilities, devices, and assistive technologies. Accessibility is an ongoing practice rather than a one-time claim of perfection.",
        ],
      },
      {
        title: "Design approach",
        body: [
          "We work toward recognized web-accessibility guidance through semantic structure, keyboard access, visible focus, readable contrast, descriptive labels, responsive layouts, and reduced-motion support where practical.",
        ],
      },
      {
        title: "Known limitations",
        body: [
          "Third-party payment pages, browser extensions, older devices, and some product imagery may present limitations outside our direct control. We welcome details about barriers encountered on any page.",
        ],
      },
      {
        title: "Request assistance",
        body: [
          `Email ${CONTACT_EMAIL} with the page address, the task you were trying to complete, and the browser or assistive technology involved if you are comfortable sharing it. We will try to provide the information or a reasonable alternative format.`,
        ],
      },
      {
        title: "Purchasing support",
        body: [
          "If an accessibility barrier prevents you from reviewing product or policy information, contact us before purchasing. We can provide available details by email, but we will never ask you to send full payment-card information by email.",
        ],
      },
      {
        title: "Continuous improvement",
        body: [
          "We review relevant feedback when updating the storefront and prioritize issues that block core tasks such as finding a product, understanding the total, or reaching support.",
        ],
      },
    ],
    related: [
      { label: "Contact", to: "/contact" },
      { label: "Terms of Service", to: "/terms" },
      { label: "Security Policy", to: "/policies/security" },
    ],
  },
  {
    slug: "responsible-sourcing",
    title: "Responsible Sourcing & Supply Chain Statement",
    eyebrow: "Practical, honest commitments",
    lead: "Principles used when considering materials, suppliers, packaging, and reports about supply-chain conduct.",
    sections: [
      {
        title: "Scope",
        body: [
          "This statement describes ioomparade's sourcing expectations. It is not a claim that every material has a third-party certification or that every supplier has been independently audited unless a product page expressly says so.",
        ],
      },
      {
        title: "Supplier expectations",
        body: [
          "We expect suppliers to comply with applicable labour, health and safety, environmental, anti-bribery, trade, and human-rights laws. Forced labour, child labour contrary to law, trafficking, unlawful discrimination, and retaliation for reporting concerns are not acceptable.",
        ],
      },
      {
        title: "Materials and claims",
        body: [
          "We aim to describe materials and certifications accurately and avoid unsupported environmental claims. Availability, durability, product safety, and overall impact are considered when selecting materials and packaging.",
        ],
      },
      {
        title: "Packaging and waste",
        body: [
          "Where practical, we seek packaging that protects the product without unnecessary material. Local recycling rules differ, so packaging should be sorted according to the instructions available where it is received.",
        ],
      },
      {
        title: "Concerns and corrective action",
        body: [
          "Reports about product origin, labour conditions, unauthorized goods, or misleading material claims may be reviewed with the relevant supplier. Depending on severity and evidence, action may include requesting information, correction, remediation, pausing purchases, or ending the relationship.",
        ],
      },
      {
        title: "Report an issue",
        body: [
          `Send a specific, good-faith concern to ${CONTACT_EMAIL}. Include the product, supplier or issue involved and any reliable supporting information. Do not obtain evidence unlawfully or put anyone at risk.`,
        ],
      },
    ],
    related: [
      { label: "Product Information & Safety", to: "/policies/product-safety" },
      { label: "Intellectual Property", to: "/policies/intellectual-property" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    slug: "complaints",
    title: "Complaints & Dispute Resolution Policy",
    eyebrow: "A fair path to resolution",
    lead: "How to raise an order, product, privacy, accessibility, or service complaint with ioomparade.",
    sections: [
      {
        title: "Contact us first",
        body: [
          `Email ${CONTACT_EMAIL} with the subject “Complaint.” Include your name, order reference if relevant, a clear description of the issue, the outcome you are seeking, and any useful non-sensitive evidence.`,
        ],
      },
      {
        title: "Acknowledgement and review",
        body: [
          "We will review the information available, may ask focused follow-up questions, and aim to respond within a reasonable time. Complex matters, carrier investigations, payment-provider reviews, or cross-border issues may take longer.",
        ],
      },
      {
        title: "Fair handling",
        body: [
          "We assess complaints in good faith and without unlawful discrimination or retaliation. We may decline abusive, threatening, knowingly false, duplicate, or unrelated communications while continuing to address any legitimate underlying issue.",
        ],
      },
      {
        title: "Possible outcomes",
        body: [
          "Depending on the facts and applicable law, an outcome may include an explanation, correction, replacement, refund, price adjustment, accessibility accommodation, privacy action, or no further action where the claim is unsupported.",
        ],
      },
      {
        title: "Escalation",
        body: [
          "If you disagree with the initial outcome, reply with the reason and any new relevant evidence and ask for reconsideration. We may arrange a second review where practical.",
        ],
      },
      {
        title: "External options",
        body: [
          "Nothing here prevents either party from using a lawful remedy. Depending on your location and the issue, you may be able to contact a consumer, privacy, payment, or other competent authority or use an authorized dispute-resolution process.",
        ],
      },
    ],
    related: [
      { label: "Consumer Rights", to: "/policies/consumer-rights" },
      { label: "Returns & Refunds", to: "/returns" },
      { label: "Privacy Choices", to: "/policies/privacy-choices" },
    ],
  },
];

const policyGroups = [
  {
    title: "Orders and payment",
    description: "What happens before, during, and after checkout.",
    links: ["orders", "payments", "cancellations", "pricing-promotions", "international-orders"],
  },
  {
    title: "Products and customers",
    description: "Product expectations, safety, remedies, and support.",
    links: ["warranty", "product-safety", "consumer-rights", "complaints"],
  },
  {
    title: "Privacy, access, and trust",
    description: "Choices and standards for using the ioomparade store.",
    links: ["privacy-choices", "security", "acceptable-use", "intellectual-property", "accessibility", "responsible-sourcing"],
  },
];

const existingPolicies = [
  { title: "Terms of Service", to: "/terms", description: "The agreement governing use of the store." },
  { title: "Privacy Policy", to: "/privacy", description: "How personal information is handled." },
  { title: "Cookie Policy", to: "/cookies", description: "Browser storage and cookie information." },
  { title: "Delivery & Shipping", to: "/shipping", description: "Processing, tracking, and delivery details." },
  { title: "Returns & Refunds", to: "/returns", description: "Eligibility, return steps, and refunds." },
];

export function PolicyDocument({ policy }) {
  return (
    <main className="ink-info-page ink-info-page--privacy policy-document">
      <div className="ink-info-page__inner">
        <header className="ink-info-page__header">
          <p className="ink-info-page__eyebrow">{policy.eyebrow}</p>
          <h1>{policy.title}</h1>
          <p className="ink-info-page__lead">{policy.lead}</p>
          <p className="policy-updated">
            Effective and last updated: <time dateTime="2026-10-16">October 16, 2026</time>
          </p>
        </header>

        <div className="privacy-layout">
          <nav className="privacy-index" aria-label={`${policy.title} sections`}>
            <p>On this page</p>
            <ol>
              {policy.sections.map((section, index) => (
                <li key={section.title}>
                  <a href={`#${policy.slug}-section-${index + 1}`}>
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    {section.title}
                  </a>
                </li>
              ))}
            </ol>
            <Link to="/policies">All store policies</Link>
          </nav>

          <div className="privacy-sections">
            {policy.sections.map((section, index) => (
              <section
                className="privacy-section"
                id={`${policy.slug}-section-${index + 1}`}
                key={section.title}
              >
                <span className="privacy-section__number" aria-hidden="true">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div>
                  <h2>{section.title}</h2>
                  {section.body.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>

        <section className="policy-related" aria-labelledby={`${policy.slug}-related-title`}>
          <div>
            <p className="ink-info-page__kicker">Keep reading</p>
            <h2 id={`${policy.slug}-related-title`}>Related policies</h2>
          </div>
          <nav aria-label={`Policies related to ${policy.title}`}>
            {policy.related.map((item) => (
              <Link key={item.to} to={item.to}>{item.label}</Link>
            ))}
          </nav>
        </section>

        <section className="privacy-contact" aria-labelledby={`${policy.slug}-contact-title`}>
          <div>
            <p className="ink-info-page__kicker">Questions or requests</p>
            <h2 id={`${policy.slug}-contact-title`}>Contact ioomparade</h2>
            <p>Please include the policy name and your order reference when relevant.</p>
          </div>
          <address>
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            <span>ioomparade — Policy Enquiry</span>
          </address>
        </section>
      </div>
    </main>
  );
}

function PolicyCenter() {
  const findPolicy = (slug) => policyDocuments.find((policy) => policy.slug === slug);

  return (
    <main className="ink-info-page ink-info-page--privacy policy-center">
      <div className="ink-info-page__inner">
        <header className="ink-info-page__header">
          <p className="ink-info-page__eyebrow">Clear information in one place</p>
          <h1>Store Policies</h1>
          <p className="ink-info-page__lead">
            Read how ioomparade handles orders, payments, delivery, returns,
            privacy, product concerns, website use, and customer rights.
          </p>
          <p className="policy-updated">
            Policy set last updated: <time dateTime="2026-10-16">October 16, 2026</time>
          </p>
        </header>

        <section className="policy-core" aria-labelledby="policy-core-title">
          <div className="policy-section-heading">
            <p className="ink-info-page__kicker">Core documents</p>
            <h2 id="policy-core-title">Start here</h2>
          </div>
          <div className="policy-card-grid">
            {existingPolicies.map((policy) => (
              <Link className="policy-card" to={policy.to} key={policy.to}>
                <h3>{policy.title}</h3>
                <p>{policy.description}</p>
                <span>Read policy <span aria-hidden="true">&#8594;</span></span>
              </Link>
            ))}
          </div>
        </section>

        {policyGroups.map((group) => (
          <section className="policy-group" key={group.title} aria-labelledby={`policy-group-${group.title.replace(/\W+/g, "-").toLowerCase()}`}>
            <div className="policy-section-heading">
              <h2 id={`policy-group-${group.title.replace(/\W+/g, "-").toLowerCase()}`}>{group.title}</h2>
              <p>{group.description}</p>
            </div>
            <div className="policy-card-grid">
              {group.links.map((slug) => {
                const policy = findPolicy(slug);
                return (
                  <Link className="policy-card" to={`/policies/${slug}`} key={slug}>
                    <h3>{policy.title}</h3>
                    <p>{policy.lead}</p>
                    <span>Read policy <span aria-hidden="true">&#8594;</span></span>
                  </Link>
                );
              })}
            </div>
          </section>
        ))}

        <section className="privacy-contact" aria-labelledby="policy-center-contact-title">
          <div>
            <p className="ink-info-page__kicker">Need help finding an answer?</p>
            <h2 id="policy-center-contact-title">Contact ioomparade</h2>
            <p>Tell us which order, product, or policy your question concerns.</p>
          </div>
          <address>
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            <span>ioomparade — Customer Care</span>
          </address>
        </section>
      </div>
    </main>
  );
}

export default PolicyCenter;
